// src/components/Navbar.js
import React, { useContext, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import { auth } from '../firebase';
import { Check, ArrowRight, Menu, X } from 'lucide-react';
import { toast } from 'react-toastify';
import PlanSelectionModal from './PlanSelectionModal';

function Navbar() {
  const { currentUser, userRole, hasValidSubscription } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    try {
      await auth.signOut();
      // Redirect to login page after logout
      window.location.href = 'https://app.reelintro.com/login';
      toast.success('Successfully logged out');
    } catch (error) {
      toast.error('Failed to log out');
    }
  };

  // Helper function to handle smooth scrolling
  const scrollToSection = (sectionId) => {
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollTo: sectionId } });
    } else {
      document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    }
    // Close mobile menu after navigation
    setMobileMenuOpen(false);
  };

  return (
    <>
      <PlanSelectionModal isOpen={planModalOpen} onClose={() => setPlanModalOpen(false)} />
      <nav className="bg-white border-b shadow-sm">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          {/* Logo / Brand - links to dashboard if logged in, marketing site if not */}
          {currentUser ? (
            <Link
              to="/dashboard/home"
              className="text-2xl font-bold text-gray-800 hover:text-gray-900"
            >
              Reel:Intro
            </Link>
          ) : (
            <a
              href="https://www.reelintro.com"
              className="text-2xl font-bold text-gray-800 hover:text-gray-900"
            >
              Reel:Intro
            </a>
          )}

          {/* Center Nav for Desktop (only for non-logged in users) */}
          {!currentUser && (
            <div className="hidden md:flex space-x-8">
              <a
                href="https://www.reelintro.com/#pricing"
                className="text-gray-600 hover:text-gray-900 transition font-medium"
              >
                Pricing
              </a>
              <a
                href="https://www.reelintro.com/#faq"
                className="text-gray-600 hover:text-gray-900 transition font-medium"
              >
                FAQ
              </a>
              <a
                href="https://www.reelintro.com/blog"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-600 hover:text-gray-900 transition font-medium"
              >
                Blog
              </a>
            </div>
          )}

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-6">
            {currentUser ? (
              <>
                {/* If admin, link to admin dashboard; otherwise user dashboard */}
                {userRole === 'admin' ? (
                  <Link
                    to="/admin/home"
                    className="text-gray-600 hover:text-gray-900 transition font-medium"
                  >
                    Admin Dashboard
                  </Link>
                ) : (
                  <>
                    {hasValidSubscription ? (
                      <Link
                        to="/dashboard/home"
                        className="text-gray-600 hover:text-gray-900 transition font-medium"
                      >
                        Dashboard
                      </Link>
                    ) : (
                      <a
                        href="https://www.reelintro.com/#pricing"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-3 py-1 rounded-md bg-yellow-100 text-yellow-800 hover:bg-yellow-200 transition font-medium flex items-center"
                      >
                        <span>Purchase a Plan</span>
                      </a>
                    )}
                  </>
                )}

                {/* Admin-only link (Manage Users) */}
                {userRole === 'admin' && (
                  <Link
                    to="/admin/users"
                    className="text-gray-600 hover:text-gray-900 transition font-medium"
                  >
                    Manage Users
                  </Link>
                )}
                
                {/* Record button - only visible for users with valid subscription */}
                {userRole !== 'admin' && hasValidSubscription && (
                  <Link
                    to="/record"
                    className="px-3 py-1 rounded-md bg-green-600 text-white hover:bg-green-700 transition font-medium"
                  >
                    Record Video
                  </Link>
                )}

                {/* Profile Link */}
                <Link
                  to="/profile"
                  className="text-gray-600 hover:text-gray-900 transition font-medium"
                >
                  Profile
                </Link>

                {/* Logout Button */}
                <button
                  onClick={handleLogout}
                  className="text-gray-600 hover:text-gray-900 transition font-medium"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                {/* If not logged in: Login only */}
                <Link
                  to="/login"
                  className="text-gray-600 hover:text-gray-900 transition font-medium"
                >
                  Login
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden focus:outline-none"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? (
              <X className="w-6 h-6 text-gray-900" />
            ) : (
              <Menu className="w-6 h-6 text-gray-900" />
            )}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200 shadow-lg">
            <div className="container mx-auto px-4 py-4 space-y-4">
              {!currentUser && (
                <>
                  <a
                    href="https://www.reelintro.com/#pricing"
                    className="block w-full text-left py-2 text-gray-700 hover:text-blue-600 transition-colors font-medium"
                  >
                    Pricing
                  </a>
                  <a
                    href="https://www.reelintro.com/#faq"
                    className="block w-full text-left py-2 text-gray-700 hover:text-blue-600 transition-colors font-medium"
                  >
                    FAQ
                  </a>
                  <a
                    href="https://www.reelintro.com/blog"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block w-full text-left py-2 text-gray-700 hover:text-blue-600 transition-colors font-medium"
                  >
                    Blog
                  </a>
                </>
              )}

              {currentUser ? (
                <>
                  {/* If admin, link to admin dashboard; otherwise user dashboard */}
                  {userRole === 'admin' ? (
                    <Link
                      to="/admin/home"
                      className="block w-full text-left py-2 text-gray-700 hover:text-blue-600 transition-colors font-medium"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Admin Dashboard
                    </Link>
                  ) : (
                    <>
                      {hasValidSubscription ? (
                        <Link
                          to="/dashboard/home"
                          className="block w-full text-left py-2 text-gray-700 hover:text-blue-600 transition-colors font-medium"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          Dashboard
                        </Link>
                      ) : (
                        <a
                          href="https://www.reelintro.com/#pricing"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block w-full text-left py-2 text-yellow-800 hover:text-yellow-900 transition-colors font-medium"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          Purchase a Plan
                        </a>
                      )}
                    </>
                  )}

                  {/* Admin-only link (Manage Users) */}
                  {userRole === 'admin' && (
                    <Link
                      to="/admin/users"
                      className="block w-full text-left py-2 text-gray-700 hover:text-blue-600 transition-colors font-medium"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Manage Users
                    </Link>
                  )}
                  
                  {/* Record button - only visible for users with valid subscription */}
                  {userRole !== 'admin' && hasValidSubscription && (
                    <Link
                      to="/record"
                      className="block w-full text-left py-2 text-green-600 hover:text-green-700 transition-colors font-medium"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Record Video
                    </Link>
                  )}

                  {/* Profile Link */}
                  <Link
                    to="/profile"
                    className="block w-full text-left py-2 text-gray-700 hover:text-blue-600 transition-colors font-medium"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Profile
                  </Link>

                  {/* Logout Button */}
                  <button
                    onClick={() => {
                      handleLogout();
                      setMobileMenuOpen(false);
                    }}
                    className="block w-full text-left py-2 text-gray-700 hover:text-blue-600 transition-colors font-medium"
                  >
                    Logout
                  </button>
                </>
              ) : (
                <>
                  {/* If not logged in: Login only */}
                  <Link
                    to="/login"
                    className="block w-full text-left py-2 text-gray-700 hover:text-blue-600 transition-colors font-medium"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Login
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </nav>
    </>
  );
}

export default Navbar;
